import { type ReactNode } from 'react'
import {
  CardContent,
  CardContentDescription,
  CardHeader,
  CardHeaderActions,
  CardTitle,
} from '@/components/Card'
import { IconButton } from '@/components/IconButton'
import { Icon } from '@/components/Icon'
import {
  Dropdown,
  DropdownItem,
  DropdownItemTitle,
  DropdownTitle,
} from '@/components/Dropdown'
import { ProgressBar } from '@/components/ProgressBar'
import { markdownToHtml } from '@/utils/markdown-util'

export interface JobProgressionCardContentProps {
  jobIsCompleted: boolean
  title?: string
  proDescription?: string
  clientDescription?: string
  hasProgressionSteps: boolean
  completionStatus: number
  pendingCompletionStatus: number
  isProviderCollaborator: boolean
  isChampionClientCollaborator: boolean
  canSetReadyToFile: boolean
  canApproveDraftAsSupportingClient?: boolean
  providerActions: ReactNode
  clientActions: ReactNode
  adminActions: ReactNode
  supportingClientActions: ReactNode
  onViewHistory: () => unknown
}

export function JobProgressionCardContent({
  jobIsCompleted,
  title,
  proDescription,
  clientDescription,
  hasProgressionSteps,
  completionStatus,
  pendingCompletionStatus,
  isProviderCollaborator,
  isChampionClientCollaborator,
  canSetReadyToFile,
  canApproveDraftAsSupportingClient,
  providerActions,
  clientActions,
  adminActions,
  supportingClientActions,
  onViewHistory,
}: JobProgressionCardContentProps) {
  return (
    <>
      <CardHeader>
        <CardTitle testId="job-progression-card-title">
          {jobIsCompleted ? 'Job completed' : title}
        </CardTitle>
        {hasProgressionSteps && (
          <CardHeaderActions>
            <Dropdown
              autoDismiss
              testId="job-progression-card-dropdown-menu"
              button={
                <IconButton
                  name="more"
                  label="Job progression actions"
                  rotate={90}
                />
              }
            >
              <DropdownTitle>Actions</DropdownTitle>
              <DropdownItem
                icon={<Icon name="clock" />}
                onPress={onViewHistory}
                testId="job-progression-view-history"
              >
                <DropdownItemTitle>View history</DropdownItemTitle>
              </DropdownItem>
            </Dropdown>
          </CardHeaderActions>
        )}
      </CardHeader>
      <CardContent>
        <ProgressBar
          key={1}
          bars={[
            {
              progress: completionStatus,
            },
            {
              progress: pendingCompletionStatus,
              style: 'striped',
            },
          ]}
        />
      </CardContent>

      {isProviderCollaborator && (
        <>
          <CardContent>
            <CardContentDescription
              dangerouslySetInnerHTML={{
                __html: markdownToHtml(
                  jobIsCompleted
                    ? 'Congratulations! There are no more tasks to complete.'
                    : (proDescription ?? ''),
                ),
              }}
            ></CardContentDescription>
          </CardContent>
          {!jobIsCompleted && providerActions}
        </>
      )}
      {isChampionClientCollaborator && (
        <>
          <CardContent>
            <CardContentDescription
              dangerouslySetInnerHTML={{
                __html: markdownToHtml(
                  jobIsCompleted
                    ? 'Congratulations! There are no more tasks to complete.'
                    : (clientDescription ?? ''),
                ),
              }}
            ></CardContentDescription>
          </CardContent>
          {!jobIsCompleted && clientActions}
        </>
      )}
      {!jobIsCompleted && canSetReadyToFile && (
        <>
          {!isProviderCollaborator && !isChampionClientCollaborator && (
            <CardContent>
              <CardContentDescription
                dangerouslySetInnerHTML={{
                  __html: markdownToHtml(
                    jobIsCompleted
                      ? 'Congratulations! There are no more tasks to complete.'
                      : (proDescription ?? ''),
                  ),
                }}
              />
            </CardContent>
          )}
          {adminActions}
        </>
      )}

      {canApproveDraftAsSupportingClient && (
        <>
          <CardContent>
            <CardContentDescription
              dangerouslySetInnerHTML={{
                __html: markdownToHtml(
                  jobIsCompleted
                    ? 'Congratulations! There are no more tasks to complete.'
                    : (clientDescription ?? ''),
                ),
              }}
            />
          </CardContent>
          {supportingClientActions}
        </>
      )}
    </>
  )
}
